
  import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
  import {
    ISideDrawerContent, FileDropzone, IDocument, FileField, DocumentService, IDocumentService, Grid, Toolbar, FullScreenToggle, ProgressBar,
    IMappingColumnOptionWithChildren, IComboBoxCellEditorCloseParams, ICellDropTargetOnDropEventParams, IGridComponent, ProgressBarType,
    GetRowIdFunc
  } from '@samc/centricity-core';
  import { IPackageDocumentSetting, IPackageDocumentRequest, IPackageDocumentResponse } from './../Interfaces';
  import { Guid, AjaxHelper, IPaginatedList, pluralize } from '@samc/common';
  import { IDocumentType } from '../../../Interfaces';

  @Component({
    name: 'open-document-side-drawer',
    components: { FileDropzone, FileField, Grid, Toolbar, FullScreenToggle, ProgressBar }
  })
  export default class OpenDocumentSideDrawerComponent extends Vue implements ISideDrawerContent {
    @Prop({ required: true }) public packageDocumentSetting!: IPackageDocumentSetting;
    @Prop({ required: true }) public verificationToken!: string;
    @Prop({ required: true }) public documentTypes!: IDocumentType[];
    @Prop({ required: true }) public packageType!: 'buy' | 'sell';
    @Prop() public addCloseProp!: (callback: (preventDefault: () => void) => any) => void;
    @Prop() public cancelProp!: () => void;
    @Prop() public actionProp!: () => void;

    public documentService!: IDocumentService;
    public isDisabled = false;
    public resultDropFiles: File[] | null = [];
    public updateFiles: File[] | null = null;
    public documents: IDocument[] | null = [];
    public uploadStatus: string | null = "Upload Status";
    public errors: number = 0;
    public success: number = 0;
    public successfulUploads: IPackageDocumentRequest[] = [];
    public gridKey: number = 0;
    public addResult: boolean = false;
    public progressValue: number = 0;
    public progressBarType: ProgressBarType = 'basic';
    public getRowId: GetRowIdFunc<IPackageDocumentRequest> = x => x.data.id;
    public documentRequestsGrid(): IGridComponent | undefined { return this.$refs.documentRequestsGrid as any; }

    public async mounted() {
      this.successfulUploads = [];
      
      this.documentService = new DocumentService(new AjaxHelper('componentapi/dataimport', this.verificationToken, '', null)) as any;
      this.addCloseProp(async () => {
        this.documentRequestsGrid()?.gridApi?.stopEditing();
        if (this.successfulUploads.length > 0 && this.successfulUploads.filter(t => t.packageDocumentTypeId == null).length > 0) {
          this.$message.alert('PackageDocument.DocumentTypeRequired', 'warning');
        } else if (this.successfulUploads.length > 0) {
          await this.addDocuments();
          if (this.addResult) {
            return this.addResult;
          }
        }
      });
    }

    public getDocumentTypeNameColumnValue(params: any) {
      if (this.documentTypes !== null) {
        var docType = this.documentTypes.filter(d => d.id == params.data.packageDocumentTypeId);
        if (docType.length > 0) {
          return docType[0].name;
        }
      }
    }

    public async onDropDocumentType(e: ICellDropTargetOnDropEventParams<IDocumentType, IPackageDocumentRequest>) {
      await this.setDocumentType(e.toData, e.fromData);
    }

    private async onCloseDocumentTypeEditor(e: IComboBoxCellEditorCloseParams<IDocumentType>) {
      await this.setDocumentType(e.params.data, e.data);
    }

    private async setDocumentType(detail: IPackageDocumentRequest, documentType?: IDocumentType) {
      const detailNode = this.documentRequestsGrid()?.gridApi?.getRowNode(detail.id) ?? null;
      if (detailNode != null && documentType != null) {
        this.successfulUploads.filter(f => f.id == detail.id).forEach(item => {
          item.packageDocumentTypeId = documentType !== undefined ? documentType.id : null;
        });

        detailNode.setDataValue('packageDocumentTypeId', documentType !== undefined ? documentType.id : null);
      }
    }

    public async onFilesChange(newFiles: File[] | null) {
      this.progressValue = 0;
      this.errors = 0;
      if (newFiles != null && newFiles?.length > 10) {
        this.uploadStatus = "You can upload maximum of 10 files in one transaction.";
        return;
      }
      this.updateFiles = newFiles;
      this.resultDropFiles = newFiles;

      if (this.documentService != null && newFiles != null) {
        this.uploadStatus = "Uploading 0/" + newFiles.length + " file" + (newFiles.length > 1 ? "s" : "");
        newFiles.forEach((newFile: File) => {
          let mimeType: string | null = null;
          if ([/\.txt$/i, /\.csv$/i].filter(x => x.test(newFile.name)).length > 0) {
            mimeType = 'text/csv';
          }
          if ([/\.xls$/i, /\.xlsx$/i].filter(x => x.test(newFile.name)).length > 0) {
            mimeType = /\.xlsx$/i.test(newFile.name) ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' : 'application/vnd.ms-excel';
          }
          if ([/\.doc$/i, /\.docx$/i].filter(x => x.test(newFile.name)).length > 0) {
            mimeType = /\.docx$/i.test(newFile.name) ? 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' : 'application/msword';
          }
          if ([/\.pdf$/i].filter(x => x.test(newFile.name)).length > 0) {
            mimeType = 'application/pdf';
          }
          if ([/\.png$/i].filter(x => x.test(newFile.name)).length > 0) {
            mimeType = 'image/png';
          }
          if ([/\.bmp$/i].filter(x => x.test(newFile.name)).length > 0) {
            mimeType = 'image/bmp';
          }
          if ([/\.jpg$/i, /\.jpeg$/i].filter(x => x.test(newFile.name)).length > 0) {
            mimeType = 'image/jpeg';
          }

          if (mimeType === null) {
            this.$log.error('Invalid Filetype');
            this.errors += 1;
            this.buildStatus();
          }
          else {
            this.documentService.createDocument(newFile, Guid.create().toString(), mimeType, this.packageDocumentSetting.type + ' Document').then((r: IDocument) => {
              this.documents?.push(r);
              this.success += 1;
              this.buildStatus();
              this.handleDocument(r);
            }, (err: any) => {
              this.$log.error(err);
              this.errors += 1;
              this.buildStatus();
            });
          }
        });
      }
    }

    public handleDocument(docomentResponse: IDocument) {
      let d: IPackageDocumentRequest = {
        documentName: docomentResponse.fileName,
        documentTag: null,
        packageId: this.packageDocumentSetting.packageId,
        id: docomentResponse.documentId,
        modifiedBy: $user ? $user?.name : '',
        createdBy: $user ? $user?.name : '',
        internalId: null,
        packageDocumentTypeId: null
      };

      this.successfulUploads.push(d);
    }

    private buildStatus() {
      const processed = (this.documents ? this.documents?.length : 0) + this.errors;
      const maxFiles = this.success + this.errors;
      
      this.progressValue = (processed * 100) / maxFiles;
      if (this.progressValue == 100) {
        this.uploadStatus = `Completed ${processed} ${pluralize(maxFiles, 'files', 'file')}`;
        if (this.errors > 0) {
          this.uploadStatus += `, with ${pluralize(this.errors, 'errors {0}', 'error {0}')}`;
        }
      } else if (this.progressValue == 0)
      {
        this.uploadStatus = "Upload";
      }
      else {
        this.uploadStatus = `Uploading ${processed}/${pluralize(maxFiles, '{0} files', '{0} file')}`;
        if (this.errors > 0) {
          this.uploadStatus += `, with ${pluralize(this.errors, 'errors {0}', 'error {0}')}`;
        }
      }
      this.handleProgressBarType();
    }

    private handleProgressBarType() {
      const maxFiles = this.success + this.errors;
      if (this.progressValue == 0)
      {
        this.progressBarType = 'basic';
      }
      else
      {
        if (this.errors === 0)
        {
          this.progressBarType = 'success';
        }
        else
        {
          if (maxFiles === this.errors)
          {
            this.progressBarType = 'error';
          }
          else
          {
            this.progressBarType = 'warning';
          }
        }
      }
    }

    public async addDocuments() {
      this.$loading('Adding documents');
      const helper = new AjaxHelper('/packagedocument/multipledocument', this.verificationToken, null, null);
      try {
        const response = await helper.postData<boolean>('', { documents: this.successfulUploads },
          { packageType: this.packageType, packageId: this.packageDocumentSetting.packageId });
        this.addResult = response;
      }
      catch (err: any) {
        this.addResult = false;
        this.$alert.error('An error occurred saving document.');
      }
      finally {
        this.$loading();
      }

      return this.addResult;
    }

    @Watch('resultDropFiles', { deep: true })
    public onResultDropFileChange(newResultDropFiles: File[] | null) {
      if (newResultDropFiles !== null) {
        // We do not care to keep a ref to this file after the drop has been complete so we null it out
        setTimeout(() => this.resultDropFiles = null);
      }
    }
  }
